import React, { useState, SVGProps } from "react";
import { Link } from "react-router-dom";
import Logo from "../adora-logo";
import Auth, { AdoraUser } from "../../utils/auth";
import { Box, Button, Flex, fluxTheme, TextField, Typography } from "flux";
import Result from "../../types/result";
import AadoraMonogram from "./adoraMonogram.svg";

type AadoraMonogramProps = SVGProps<SVGSVGElement>;
const StyledAadoraMonogram = AadoraMonogram as unknown as React.FC<AadoraMonogramProps>;

export interface LoginProps {
  onLogin: (user: Result<AdoraUser>) => void;
  subTitle?: string;
}

const Login: React.FC<LoginProps> = ({ onLogin, subTitle = "" }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSigninHappening, setIsSigninHappening] = useState(false);
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsSigninHappening(true);
    setError("");

    const result = await Auth.login(new FormData(event.currentTarget));
    if (result.isError() && result.error.message !== "Brand is not active") {
      setError("Invalid username or password");
    }

    onLogin(result);

    setIsSigninHappening(false);
  };

  return (
    <Flex height="100%">
      <Flex
        height="100vh"
        width="33.333333%"
        maxWidth="428px"
        alignItems="center"
        flexDirection="column"
        justifyContent="center"
        bgcolor={subTitle ? "#9ABDDC" : "secondary.main"}
        position="relative"
        overflow="hidden"
      >
        <>
          <Box sx={{ transform: "translateY(-100%)" }}>
            <Logo width={"243px"} fill={fluxTheme.palette.primary.contrastText} />
          </Box>
          <Box sx={{ position: "absolute", bottom: 0, left: 0, transform: "translate(-14%, 10%)" }}>
            <Logo width={"877px"} fill={fluxTheme.palette.primary.contrastText} opacity={0.05} />
          </Box>
          <Box sx={{ position: "absolute", bottom: "100px", left: "20px" }}>
            <StyledAadoraMonogram width={"96px"} fill={fluxTheme.palette.primary.contrastText} />
          </Box>
          <Box sx={{ position: "absolute", bottom: "20px", left: "20px", width: "250px" }}>
            <Typography variant="body2" sx={{ color: fluxTheme.palette.primary.contrastText }}>
              <span style={{ fontWeight: 800 }}>Adora</span> is the AI performance marketing platform built for one
              brand: yours
            </Typography>
          </Box>
        </>

        <Typography variant="subtitle1" sx={{ color: fluxTheme.palette.primary.contrastText }}>
          {subTitle}
        </Typography>
      </Flex>
      <Flex flex="1" flexDirection="column" alignItems="center" justifyContent="center" padding="1rem">
        <Box padding="4rem">
          <Typography variant="h1" color="textPrimary" sx={{ fontSize: "80px", lineHeight: "80px" }}>
            Welcome
          </Typography>
          <Flex justifyContent="center">
            <Typography variant="subtitle1" color="textSecondary">
              {subTitle}
            </Typography>
          </Flex>
        </Box>
        <form onSubmit={handleSubmit}>
          <Flex minWidth="400px" flexDirection="column" gap="24px">
            <TextField
              type="text"
              name="username"
              fullWidth
              value={username}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
              placeholder="Username"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "40px", height: "48px" } }}
              dataDogIdentifier="login username"
            />
            <TextField
              type="password"
              name="password"
              value={password}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              placeholder="Password"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "40px", height: "48px" } }}
              dataDogIdentifier="login password"
            />
            {error && (
              <Typography variant="subtitle1" color="error">
                {error}
              </Typography>
            )}
            <Button
              color={"secondary"}
              variant="contained"
              type="submit"
              size={"large"}
              disabled={isSigninHappening}
              loading={isSigninHappening}
              cornerType={"rounded"}
              dataDogIdentifier="login"
            >
              Sign In
            </Button>
          </Flex>
        </form>
        <Box pt="28px" color="textLink.main">
          <Link to="https://www.adora-ai.com/account-access">Create Account</Link>
        </Box>
      </Flex>
    </Flex>
  );
};

export default Login;
